<template>
    <div class="w-full content-height-full">
        <shape-modal @removedShape="removeShape" @editShape="editShape" />
        <delivery-project-details />
        <mdu-details />
        <address-details @updatedAddress="updateAddress" />

        <gmap-map
            ref="map"
            style="height: 100%;"
            :center="center"
            :zoom="10"
            @bounds_changed="needsNewAddresses = true"
            @idle="updateCanUpdateAddresses"
        >
            <delivery-projects-renderer
                v-if="mapLoaded"
                :drawing-manager="drawingManager"
                :can-edit="false"
                :delivery-projects="deliveryProjectPaths"
                :current-delivery-project="deliveryProject"
                @rendered="initializeCriticalMomentItems"
                @currentDeliveryProjectDrawn="(shape) => deliveryProjectShape = shape"
            />
            <gmap-cluster :minimum-cluster-size="300">
                <gmap-marker
                    v-for="(marker, index) in addresses"
                    :key="index"
                    :icon="getDeliveryAddressIcon(marker, false)"
                    :position="markerPosition(marker)"
                    :clickable="true"
                    :draggable="false"
                    @click="showMduDetails(marker)"
                />
                <gmap-marker
                    v-for="(marker, index) in addressesBelongingToDeliveryProject"
                    :key="`project-${index}`"
                    :icon="getDeliveryAddressIcon(marker, true)"
                    :position="markerPosition(marker)"
                    :clickable="true"
                    :draggable="false"
                    @click="showMduDetails(marker)"
                />
            </gmap-cluster>
        </gmap-map>

        <div class="absolute pin-l pin-t ml-2xl" style="margin-top: 106px;">
            <div class="critical-moments-list">
                <button
                    v-for="type in markerTypes"
                    class="critical-moment-item"
                    :class="{ 'border-white': selectedType === type, 'border-neutral-darkest': selectedType !== type }"
                    :disabled="selectedType !== null && selectedType.type !== 'marker'"
                    @click="selectType(type)"
                >
                    <img :src="type.sidebar_icon" class="w-lg h-lg mr-sm">
                    <span class="text-left">{{ type.name }}</span>
                </button>

                <hr class="border-t border-neutral-lightest m-lg">

                <button
                    v-for="type in lineTypes"
                    class="critical-moment-item"
                    style="background-color: rgba(46, 62, 79, 0.8);"
                    :class="{ 'border-white': selectedType === type, 'border-neutral-darkest': selectedType !== type }"
                    :disabled="selectedType !== null && (selectedType !== type && selectedType.type !== 'marker')"
                    @click="selectType(type)"
                >
                    <span
                        class="inline-block rounded-full border-xs border-white mr-sm"
                        :style="`background: ${type.meta.color}`"
                        style="height: 12px; width: 12px;"
                    />

                    <span v-if="selectedType !== null && selectedType === type">
                        Spara
                    </span>
                    <span v-else>
                        {{ type.name }}
                    </span>
                </button>
            </div>
            <div class="mt-lg p-xl bg-neutral-darkest text-white rounded" style="width: 312px; background-color: rgba(46, 62, 79, 0.8);">
                <table class="w-full text-sm">
                    <tr>
                        <td class="font-bold">Pris totalt</td>
                        <td>{{ formatPrice(totalPrice) }}</td>
                    </tr>
                    <tr>
                        <td class="font-bold">Pris inom område</td>
                        <td>{{ formatPrice(priceInShape) }}</td>
                    </tr>
                    <tr>
                        <td class="font-bold">Antal hushåll</td>
                        <td>{{ addressesCount }} st</td>
                    </tr>
                </table>

                <button
                    class="mt-lg btn is-ghost is-primary border border-white text-white hover:text-white btn-lg w-full"
                    :disabled="! canUpdateAddresses || loading"
                    @click="fetch"
                >
                    <span v-if="! loading">Ladda adresser</span>
                    <span v-else>
                        <font-awesome-icon icon="spinner" spin />
                        Laddar adresser
                    </span>
                </button>

                <button
                    v-if="! deliveryProject.polygon_path"
                    class="btn is-primary btn-lg w-full mt-md"
                    @click="selectType({ type: 'polygon', name: 'Markera område' })"
                >
                    Markera område
                </button>
                <button
                    v-else-if="! editingDeliveryProjectBoundaries"
                    class="btn is-primary btn-lg w-full mt-md"
                    @click="editDeliveryProjectShape"
                >
                    Redigera område
                </button>
                <button
                    v-else-if="isSavingDeliveryProjectArea"
                    class="btn is-ghost is-primary btn-lg w-full mt-md"
                >
                    <span>
                        <font-awesome-icon icon="spinner" spin />
                        Sparar område&hellip;
                    </span>
                </button>
                <button
                    v-else
                    class="btn is-primary btn-lg w-full mt-md"
                    @click="saveDeliveryProjectShape"
                >
                    Spara område
                </button>
                <button
                    v-if="! isPolygonValid"
                    class="btn is-danger btn-lg w-full mt-md"
                    @click="deleteDeliveryProjectShape"
                >
                    Ta bort område
                </button>
            </div>
        </div>
        <div class="pointer-events-none p-lg z-40 absolute pin-t pin-x mt-2xl flex justify-center" style="max-width: 600px;">
            <div
                class="border border-utility py-sm px-lg bg-white rounded-full flex items-center mr-md"
            >
                <font-awesome-icon icon="info-circle" class="text-utility mr-sm" />

                <span v-if="temporaryInfoText" v-text="temporaryInfoText" />
                <span v-else-if="editingDeliveryProjectBoundaries">
                    Pris inom område uppdateras igen när området är sparat.
                </span>
                <span v-else-if="! needsNewAddresses && addressesLoadedAtLeastOnce && ! loading">
                    Din karta är uppdaterad med adresser
                </span>
                <span v-else-if="canUpdateAddresses && ! addressesLoadedAtLeastOnce">
                    Du kan nu ladda adresser genom att trycka på knappen "Ladda adresser".
                </span>
                <span v-else-if="! addressesLoadedAtLeastOnce">
                    Du kan ladda adresser när du är tillräckligt inzoomad på kartan.
                </span>
                <span v-else-if="! loading">
                    Klicka på knappen för att ladda nya adresser om du flyttat kartan.
                </span>
                <span v-else class="ml-auto">
                    <font-awesome-icon icon="spinner" spin />
                    <em>Laddar...</em>
                </span>
            </div>
        </div>
        <sales-projects-list />
        <button
            v-if="shapeBeingEdited"
            class="btn is-primary btn-lg absolute pin-b pin-x mb-2xl"
            @click="savePolyline(shapeBeingEdited)"
        >
            Spara {{ shapeBeingEdited.meta.type }}
        </button>
    </div>
</template>
<script>
    import AddressesLocator from '../../Maps/AddressesLocator';
    import SalesProjectsList from '../Sidebars/SalesProjectsList';

    import Calculations from '../../Maps/Calculations';
    import Drawing from '../../Maps/Drawing';
    import Coordinates from '../../Maps/Coordinates';
    import Shapes from '../../Maps/Shapes';
    import ShapeModal from './ShapeModal';
    import filter from 'lodash-es/filter';
    import DeliveryProjectsRenderer from '../DeliveryProjectsRenderer';
    import DeliveryProjectDetails from './DeliveryProjectDetails';
    import MduDetails from './MduDetails';
    import AddressDetails from './../../../../shared/components/Addresses/AddressDetails/AddressDetails';

    export default {
        components: {
            ShapeModal,
            DeliveryProjectsRenderer,
            DeliveryProjectDetails,
            SalesProjectsList,
            MduDetails,
            AddressDetails,
        },
        extends: AddressesLocator,
        props: {
            markers: {
                type: Array,
                required: true,
            },
            lines: {
                type: Array,
                required: true,
            },
            types: {
                type: Array,
                required: true,
            },
            initialDeliveryProject: {
                type: Object,
                required: true,
            },
            deliveryProjects: {
                type: Array,
                required: true,
            },
            center: {
                required: false,
                type: Object,
                default: () => {
                    return window.defaultMapCenter;
                },
            },
            currentDeliveryProjectColors: {
                required: false,
                type: Object,
                default: () => {
                    return {
                        border: '#0e9919',
                        fillColor: 'rgba(14, 153, 25, 0.7)',
                    };
                },
            },
            initialIsPolygonValid: {
                required: false,
                type: Boolean,
                default: () => {
                    return true;
                },
            },
        },
        data() {
            return {
                addressesCount: 0,
                editingDeliveryProjectBoundaries: false,
                shapeBeingEdited: null,
                deliveryProject: this.initialDeliveryProject,
                priceInShape: null,
                deliveryProjectShape: null,
                salesProjectAddresses: null,
                path: '',
                mapLoaded: false,
                shapes: {},
                addresses: [],
                addressesBelongingToDeliveryProject: [],
                selectedType: null,
                needsNewAddresses: false,
                canUpdateAddresses: false,
                temporaryInfoText: null,
                switchedType: false,
                isPolygonValid: this.initialIsPolygonValid,
                isSavingDeliveryProjectArea: false,
            };
        },
        computed: {
            allAddresses() {
                return {
                    ... this.addressesBelongingToDeliveryProject,
                    ... this.addresses,
                };
            },
            deliveryProjectPaths() {
                return this.deliveryProjects.map((deliveryProject) => {
                    return {
                        path: deliveryProject.polygon_path,
                        id: deliveryProject.id,
                        name: deliveryProject.name,
                    };
                });
            },
            lineTypes() {
                return this.types.filter((type) => {
                    return type.type === 'polyline';
                });
            },
            markerTypes() {
                return this.types.filter((type) => {
                    return type.type === 'marker';
                });
            },
            totalPrice() {
                let price = 0;
                Object.entries(this.shapes).forEach(([key, shape]) => {
                    if (! shape) {
                        return;
                    }

                    price += shape.meta.price;
                });

                return price;
            },
        },
        watch: {
            switchedType(switchedType) {
                // This is used to figure out if we recently
                // switched type in other places (adding shapes
                // as an example)

                if (switchedType) {
                    this.$nextTick(() => {
                        this.switchedType = false;
                    });
                }
            },
            editingDeliveryProjectBoundaries(isEditing) {
                if (! isEditing) {
                    this.updatePriceInDeliveryProject();
                }
            },
            shapes() {
                this.updatePriceInDeliveryProject();
            },
            addresses() {
                this.updateCanUpdateAddresses();
            },
            needsNewAddresses() {
                this.updateCanUpdateAddresses();
            },
            selectedType(newType) {
                if (newType === null) {
                    this.setDrawingMode(null);
                    return;
                }

                if (newType.type === 'polyline') {
                    this.setDrawingMode('polyline');
                    if (newType.meta.dashed) {
                        this.drawingManager.drawingManager.setOptions({
                            polylineOptions: {
                                icons: [{
                                    icon: {
                                        path: 'M 0,-1 0,1',
                                        strokeOpacity: 1,
                                        scale: 4,
                                    },
                                    offset: '0',
                                    repeat: '20px',
                                }],
                                strokeColor: newType.meta.color,
                                strokeOpacity: .5,
                            },
                        });
                    } else {
                        this.drawingManager.drawingManager.setOptions({
                            polylineOptions: {
                                strokeColor: newType.meta.color,
                                strokeOpacity: 1,
                            },
                        });
                    }

                    return;
                }

                if (newType.type === 'marker') {
                    return this.setDrawingMode('marker');
                }

                this.setDrawingMode('polygon');
                this.drawingManager.drawingManager.setOptions({
                    polygonOptions: {
                        strokeColor: this.currentDeliveryProjectColors.border,
                        fillColor: this.currentDeliveryProjectColors.fillColor,
                    },
                });
            },
        },
        created() {
            this.$nextTick(() => {
                this.$refs.map.$mapPromise.then(() => {
                    this.initializeDrawing();
                    this.shapesManager = new Shapes();
                    this.coordinatesManager = new Coordinates();
                    this.mapLoaded = true;
                });
            });

            EventBus.$on('sales-project-selected', (id) => {
                this.fetchAndZoomToSalesProject(id);
            });

            EventBus.$on('delivery-project-clicked', (id) => {
                this.$modal.show('delivery-project-details', id);
            });

            EventBus.$on('remove-address-marker', (detachedAddressId) => {
                this.addressesBelongingToDeliveryProject = this.addressesBelongingToDeliveryProject
                    .filter(address => address.id !== detachedAddressId);
            });

            this.updatePriceInDeliveryProject();
            this.fetchAddressesWithinDeliveryProject();
        },
        methods: {
            showMduDetails(marker) {
                const mduAddresses = this.getMduAddresses(marker, this.allAddresses);
                if (mduAddresses.length > 1) {
                    this.$modal.show('mdu-details', mduAddresses);
                } else {
                    this.$modal.show('address-details', marker);
                }
            },
            initializeCriticalMomentItems() {
                this.setUpInitialMarkers();
                this.setUpInitialPolylines();
                this.drawingManager.fitToShape(this.initialDeliveryProject.id);
            },
            async fetchAddressesWithinDeliveryProject() {
                if (! this.deliveryProject) {
                    return;
                }

                try {
                    const result = await axios.get(route('api.delivery-projects.addresses', this.deliveryProject.id));
                    this.addressesBelongingToDeliveryProject = result.data;
                } catch (error) {
                    flash_error('Kunde inte ladda adresser för projektet, testa ladda om sidan.');
                }
            },
            async fetchAndZoomToSalesProject(id) {
                // We'll only fetch these addresses in order to be able to zoom them
                // since we'll fetch the adresses within the zoomed viewport later
                // we'll just store these in a separate data property in order to not
                // render the markers once with this data and then again later from
                // the fetch()-call.
                const result = await axios.get(route('api.addresses.inSalesProject', id));
                this.salesProjectAddresses = result.data;

                if (this.$refs.map && window.google && this.salesProjectAddresses.length > 0) {
                    const map = this.$refs.map;
                    const bounds = new google.maps.LatLngBounds();

                    this.salesProjectAddresses.forEach(marker => {
                        if (marker.lat && marker.lng) {
                            const position = new google.maps.LatLng(marker.lat, marker.lng);
                            bounds.extend(position);
                        }
                    });

                    map.$mapObject.fitBounds(bounds);
                }

                // We fetch after zooming to make sure the user sees all addresses
                // even if they don't belong to the sales project we just loaded.
                this.fetch();
            },
            async updatePriceInDeliveryProject() {
                try {
                    const response = await axios.get(route('api.delivery-projects.price', this.deliveryProject));
                    this.priceInShape = response.data.price;
                    this.addressesCount = response.data.addresses_count;
                } catch (error) {
                    flash_error('Kunde inte uppdatera priset');
                }
            },
            setShapeHoverText(shape) {
                if (shape.meta.distance) {
                    this.temporaryInfoText = `${shape.meta.type} (${shape.meta.distance}m): ${this.formatPrice(shape.meta.price)}`;
                    return;
                }

                this.temporaryInfoText = `${shape.meta.type}: ${this.formatPrice(shape.meta.price)}`;
            },
            formatPrice(price) {
                const formatter = new Intl.NumberFormat('sv-SE', {
                    style: 'currency',
                    currency: 'SEK',
                    currencyDisplay: 'symbol',
                    minimumFractionDigits: 0,
                });

                return formatter.format(price);
            },
            async fetch() {
                await this.fetchAddressesWithinViewport(this.$refs.map);
                this.fetchAddressesWithinDeliveryProject();
                this.needsNewAddresses = false;
            },
            updateCanUpdateAddresses() {
                if (! this.$refs.map.$mapObject) {
                    return;
                }

                this.canUpdateAddresses = this.$refs.map.$mapObject.zoom > 14;

                if (! this.needsNewAddresses || ! this.mapLoaded) {
                    this.canUpdateAddresses = false;
                }
            },
            setDrawingMode(mode) {
                this.drawingManager.drawingManager.setOptions({
                    drawingMode: mode,
                });
            },
            initializeDrawing() {
                this.drawingManager = new Drawing(this.$refs.map.$mapObject, {
                    drawingControl: false,
                });
                this.drawingManager.initializeDrawing();

                this.drawingManager.finishedDrawing((shape, type) => {
                    this.path = shape;

                    if (type === google.maps.drawing.OverlayType.MARKER) {
                        this.addMarker();
                    }

                    if (type === google.maps.drawing.OverlayType.POLYLINE) {
                        this.addPolyline();
                    }

                    if (type === google.maps.drawing.OverlayType.POLYGON) {
                        this.saveDeliveryProjectArea();
                    }
                });
            },
            editDeliveryProjectShape() {
                this.deliveryProjectShape.setEditable(true);
                this.editingDeliveryProjectBoundaries = true;
                this.selectedType = null;
            },
            async saveDeliveryProjectShape() {
                const result = await this.saveDeliveryProjectArea(this.deliveryProjectShape);

                if (result) {
                    this.deliveryProjectShape.setEditable(false);
                    this.editingDeliveryProjectBoundaries = false;

                    // For some reason the colors are reset here, we set them again to ensure proper
                    // border and background.
                    this.shapesManager.setBackground(
                        this.deliveryProjectShape,
                        this.currentDeliveryProjectColors.fillColor,
                    );

                    this.shapesManager.setBorder(
                        this.deliveryProjectShape,
                        this.currentDeliveryProjectColors.border,
                    );

                    if (this.addresses.length > 0) {
                      this.fetch();
                    }
                }
            },
            deleteDeliveryProjectShape() {
                if (window.confirm('Radera område?')) {
                    this.deleteDeliveryProjectArea();
                }
            },
            addPolyline() {
                let type = this.selectedType;
                if (this.switchedType) {
                    type = this.switchedType;
                }

                if (type === null) {
                    this.path.setMap(null);
                    return;
                }

                this.shapesManager.setMetaData(this.path, {
                    price: this.calculatePriceForPolyline(this.path, type),
                    distance: this.getDistanceForPolyline(this.path),
                    type: type.name,
                    type_id: type.id,
                    color: type.meta.color,
                    dashed: !! type.meta.dashed,
                    id: Math.floor(Math.random() * (99999 - 1) + 1),
                });

                this.addShape(this.path, 'polyline');

                flash_success('Dina ändringar har sparats!');
            },
            addMarker() {
                this.path.setIcon({
                    url: this.selectedType.map_icon,
                    scaledSize: new google.maps.Size(24, 24),
                });

                this.shapesManager.setOptions(this.path, {
                    comments: [],
                });

                this.shapesManager.setMetaData(this.path, {
                    price: this.selectedType.price_per_unit,
                    id: Math.floor(Math.random() * (99999 - 1) + 1),
                    type: this.selectedType.name,
                });

                this.addShape(this.path, 'marker');
            },
            async addShape(shape, type) {
                this.$set(this.shapes, shape.meta.id, shape);

                const coordinates = this.coordinatesManager.getCoordinatesFromShape(shape);
                if (! coordinates) {
                    return;
                }

                const response = await axios.post(route('api.delivery-projects.critical-moment-items.store'), {
                    shape: coordinates,
                    meta: shape.meta,
                    delivery_project_id: this.deliveryProject.id,
                    shape_type: type,
                });

                // Updates the id of the shape from the temporary id
                // to the new persisted id in the database
                this.shapesManager.setMetaData(
                    shape,
                    {
                        ... shape.meta,
                        ... { id: response.data.id },
                    },
                );

                shape.addListener('mouseover', () => {
                    this.setShapeHoverText(shape);
                });

                shape.addListener('mouseout', () => {
                    this.temporaryInfoText = null;
                });

                shape.addListener('click', () => {
                    this.toggleRemovalModal(shape);
                });

                this.selectedType = null;
            },
            getType(name) {
                return this.types.find((type) => {
                    return type.name === name;
                });
            },
            setUpInitialMarkers() {
                this.markers.forEach((marker) => {
                    const coordinates = marker.shape.coordinates;
                    const comments = marker.comments;

                    const options = {
                        meta: marker.meta,
                        icon: {
                            url: this.getType(marker.meta.type).map_icon,
                            scaledSize: new google.maps.Size(24, 24),
                        },
                        comments: comments,
                    };

                    // Temporary override from the auto generated id when first created
                    options.meta.id = marker.id;

                    const shape = this.drawingManager.addMarker(
                        new google.maps.LatLng(coordinates[1], coordinates[0]),
                        options,
                    );

                    shape.addListener('click', () => {
                        this.toggleRemovalModal(shape);
                    });

                    shape.addListener('mouseover', () => {
                        this.setShapeHoverText(shape);
                    });

                    shape.addListener('mouseout', () => {
                        this.temporaryInfoText = null;
                    });

                    this.$set(this.shapes, shape.meta.id, shape);
                });
            },
            setUpInitialPolylines() {
                this.lines.forEach((line) => {
                    const coordinates = line.shape.coordinates;
                    const comments = line.comments;

                    const coordinatesArray = [];
                    coordinates.forEach((coordinate) => {
                        coordinatesArray.push(new google.maps.LatLng(coordinate[1], coordinate[0]));
                    });

                    const options = {
                        meta: line.meta,
                        strokeColor: line.meta.color,
                        clickable: true,
                        comments: comments,
                    };

                    if (line.meta.dashed) {
                        options.icons = [{
                            icon: {
                                path: 'M 0,-1 0,1',
                                strokeOpacity: 1,
                                scale: 4,
                            },
                            offset: '0',
                            repeat: '20px',
                        }];
                        options.strokeOpacity = .5;
                    } else {
                        options.strokeOpacity = 1;
                    }

                    // Temporary override from the auto generated id when first created
                    options.meta.id = line.id;

                    const shape = this.drawingManager.addPolyline(
                        coordinatesArray,
                        options,
                    );

                    shape.addListener('click', () => {
                        this.toggleRemovalModal(shape);
                    });

                    shape.addListener('mouseover', () => {
                        this.setShapeHoverText(shape);
                    });

                    shape.addListener('mouseout', () => {
                        this.temporaryInfoText = null;
                    });

                    this.$set(this.shapes, shape.meta.id, shape);
                });
            },
            toggleRemovalModal(shape) {
                this.$modal.show('remove-shape', { shape });
            },
            async removeShape(shape) {
                await axios.delete(route(
                    'api.delivery-projects.critical-moment-items.destroy',
                    shape.meta.id,
                ));

                Object.keys(this.shapes).forEach((key) => {
                    const s = this.shapes[key];
                    if (s.meta.id === shape.meta.id) {
                        this.$delete(this.shapes, key);
                    }
                });

                this.drawingManager.resetShapes([shape]);
                this.$modal.hide('remove-shape');
                flash_success('Objekt borttaget!');
            },
            editShape(shape) {
                this.shapeBeingEdited = shape;
                this.shapesManager.enableEditing(shape);
            },
            async savePolyline(shape) {
                try {
                    const url = route(
                        'api.delivery-projects.critical-moment-items.update',
                        shape.meta.id,
                    );

                    const meta = shape.meta;
                    meta.distance = this.getDistanceForPolyline(shape);
                    meta.price = this.calculatePriceForPolyline(shape);

                    await axios.patch(
                        url,
                        {
                            shape: this.coordinatesManager.getCoordinatesFromShape(shape),
                            meta,
                        },
                    );
                } catch (e) {
                    flash_error('Kunde tyvärr inte spara, försök gärna igen');
                    return;
                }

                this.shapeBeingEdited = null;
                this.shapesManager.disableEditing(shape);
                this.updatePriceInDeliveryProject();
                flash_success('Sparat!');
            },
            getDistanceForPolyline(shape) {
                const calc = new Calculations();
                return calc.getLength(shape.getPath());
            },
            calculatePriceForPolyline(shape, type) {
                if (typeof type === 'undefined') {
                    type = this.types.find((type) => type.id === shape.meta.type_id);
                }

                const distance = this.getDistanceForPolyline(shape);
                return (Math.round(distance * 100) / 100) * type.price_per_unit;
            },
            getShapesToKeep(dontKeepId) {
                return filter(this.shapes, function (shape) {
                    return shape.meta.id !== dontKeepId;
                });
            },
            async saveDeliveryProjectArea(shape) {
                this.isSavingDeliveryProjectArea = true;
                this.temporaryInfoText = null;

                try {
                    let coordinates = null;
                    if (! shape) {
                         coordinates = this.coordinatesManager.getCoordinatesFromShape(this.path);
                         this.deliveryProjectShape = this.path;
                    } else {
                        coordinates = this.coordinatesManager.getCoordinatesFromShape(shape);
                    }

                    if (coordinates.length < 3) {
                        this.path.setMap(null);
                        return;
                    }

                    const response = await axios.post(route('api.delivery-project-areas.update', this.deliveryProject.id), {
                        coordinates: this.coordinatesManager.getStringifiedCoordinates(coordinates),
                    });

                    this.deliveryProject = response.data;

                    flash_success('Dina ändringar har sparats!');
                    this.selectedType = null;
                    this.updatePriceInDeliveryProject();
                    this.fetchAddressesWithinDeliveryProject();

                    return true;
                } catch (error) {
                    this.temporaryInfoText = 'Kunde inte spara området. Fortsätt redigera eller ta bort.';

                    return  false;
                } finally {
                    this.isSavingDeliveryProjectArea = false;
                }
            },
            async deleteDeliveryProjectArea() {
                try {
                    const response = await axios.delete(route('api.delivery-project-areas.delete', this.deliveryProject.id));

                    this.deliveryProject = response.data;
                    this.deliveryProjectShape.setMap(null);
                    this.isPolygonValid = true;

                    flash_success('Området har raderats.');

                    this.selectedType = null;
                    this.updatePriceInDeliveryProject();
                    this.fetchAddressesWithinDeliveryProject();
                } catch (error) {
                    this.path.setMap(null);

                    flash_error('Kunde inte radera området.');
                }
            },
            selectType(type) {
                if (this.selectedType !== null) {
                    this.switchedType = this.selectedType;
                }

                if (this.selectedType === type) {
                    this.selectedType = null;
                    return;
                }

                this.selectedType = type;
            },
            updateAddress(updatedAddress) {
                this.addressesBelongingToDeliveryProject.forEach((address, index) => {
                    if (address.id === updatedAddress.id) {
                        this.$set(this.addressesBelongingToDeliveryProject, index, updatedAddress);
                    }
                });

                this.addresses.forEach((address, index) => {
                    if (address.id === updatedAddress.id) {
                        this.$set(this.addresses, index, updatedAddress);
                    }
                });
            },
        },
    };
</script>
